import { debounce } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import { MapBox, mapboxgl, useTracking } from '@project44/ui-utils';
import {
  GeofenceResponse,
  TrackedShipmentPosition,
  TrackedShipmentStop,
} from '@project44-models/shipment-entity';

import { MapMarker } from '../../../../utils/helpers/shipmentMap';
import {
  drawGeofence,
  drawPingsControl,
  drawPopup,
  drawZoomControls,
} from './draw';
import { drawShipmentMapElements } from './drawShipmentMap';
import { trackMapEvents } from './track';

import { Wrapper } from './styles';

interface ShipmentRoute {
  id?: string;
  stops: TrackedShipmentStop[];
  markers: MapMarker[];
  geofences: GeofenceResponse[];
  positions: TrackedShipmentPosition[];
  currentPosition?: TrackedShipmentPosition;
  currentSegmentId: string | null;
  icon: string;
}

interface DisplayMapProps {
  routes: ShipmentRoute[];
  togglePings: () => void;
  onChangeActivePing: (p: TrackedShipmentPosition) => void;
  watch?: (cb: (p: TrackedShipmentPosition) => void) => void;
  isChina?: boolean;
}

export const DisplayMap = ({
  isChina,
  routes,
  togglePings,
  onChangeActivePing,
  watch,
}: DisplayMapProps) => {
  const trackEvents = useTracking();
  const resizeWrapper = useRef<null | HTMLDivElement>(null);
  const [map, setMap] = useState<mapboxgl.Map | null>(null);

  const allPositions = routes.flatMap((i) => i.positions);
  const allStops = routes.flatMap((i) => i.stops);

  useEffect(() => {
    if (!map) return;

    routes.forEach(({ geofences }) => {
      geofences.forEach((geofence) => drawGeofence(map, geofence));
    });
  }, [map, routes]);

  const onLoad = (currentMap: mapboxgl.Map) => {
    const resize = new ResizeObserver(debounce(() => currentMap.resize(), 100));
    if (resizeWrapper?.current) resize.observe(resizeWrapper.current);

    routes.forEach(
      ({ stops, markers, positions, currentPosition, icon, id }) => {
        drawShipmentMapElements(
          currentMap,
          id,
          markers,
          currentPosition,
          stops,
          positions,
          onChangeActivePing,
          icon,
        );
      },
    );

    trackMapEvents(trackEvents.track, currentMap, allStops.length);
    drawPingsControl(currentMap, togglePings, allPositions.length === 0);
    drawZoomControls(currentMap);

    setMap(currentMap);
  };

  watch?.((p: TrackedShipmentPosition) => {
    if (!map) return;
    map.fire('closeAllPopups');
    drawPopup(map, p, onChangeActivePing);
    map.flyTo({
      center: [Number(p.longitude), Number(p.latitude)],
      duration: 1000,
    });
  });

  return (
    <Wrapper data-testid="entity-map" ref={resizeWrapper}>
      <MapBox onLoad={onLoad} isChina={isChina} />
    </Wrapper>
  );
};
